import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaCheck, FaMinus, FaTrash, FaPlus } from 'react-icons/fa';
import usePageTitle from 'hooks/usePageTitle';
import Scroller from 'ui-component/Scroller';
import { TbDatabaseExclamation } from 'react-icons/tb';
import toast from 'react-hot-toast';
import { Modal, Button, Dropdown } from 'react-bootstrap';
import NoImage from 'assets/images/no-image.png';
import moment from 'moment';
import ReceiptModal from './components/ReceiptModal';
import { Skeleton, Tooltip } from '@mui/material';
import usePermissions from 'hooks/usePermissions';
import { getCurrencySymbol, numberFormatter } from 'utils/currency';
import useIsMobile from 'utils/isMobileUtils';
import ReceiptModalMobile from './components/ReceiptModalMobile';
import { ChevronLeftIcon } from 'ui-component/custom-icons/icons';


const PurchasingReceiptForm = ({ edit }) => {
	const { id } = useParams();
	const history = useNavigate();
	const [setPageTitle] = usePageTitle('Receipts');
	const [purchaseInvoice, setPurchaseInvoice] = useState(null);
	const [show, setShow] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
	const [nextReference, setNextReference] = useState();
	const [allPermissions, hasEveryPermission] = usePermissions();
	const [showAttachmentActions, setShowAttachmentActions] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		return () => {
			setPageTitle(null);
		};
	}, []);

	useEffect(() => {
		initializeData();
    }, [id]);
    
	const totalQuantity = purchaseInvoice?.purchase_invoice_items?.reduce((total, row) => total + row.quantity, 0);
	const initializeData = () => {


		if (edit)
		{
				api.getReceiptDetails(id).then((res) => {
					if (res?.status) {
						setNextReference(res?.data?.receipt_number)



						if (res?.data?.receipt_items.length > 0) {
							// setPurchaseOrderItems(res?.data?.purchase_invoice_items)
		
							const info = {
								id: res?.data?.id,
								purchase_order_id: res?.data?.purchase_order_id,
								purchase_invoice_number: res?.data?.purchase_invoice_number,
								purchase_invoice_id: res?.data?.purchase_invoice_id,
								date: res?.data?.date,
								currency: res?.data?.currency,
								supplier_id: res?.data?.supplier_id,
								supplier_name: res?.data?.supplier_name,
								warehouse_id: res?.data?.warehouse_id,
								warehouse_name: res?.data?.warehouse_name,
								created_by: res?.data?.created_by,
								attachmentExtension: res?.data?.default_attachment?.extension,
								attachmentUrl: res?.data?.default_attachment?.url,
								attachmentId: res?.data?.default_attachment?.id,
							}
							setPurchaseInvoice(info)
							const populate = res?.data?.receipt_items?.map(item => {
								const total = item.quantity * parseFloat(item.unit_price ?? 0);
								return {
									id: item.id,
									product_id: item.product_id,
									sku: item?.sku,
									product_name: item?.product_name,
									color_name: item?.selectedColorName,
									color_id: item?.selectedColorId,
									thumbnail_url: item?.thumbnail_url,
									custom_thumbnail_url: item?.custom_thumbnail_url,
									quantity: item?.quantity,
									unit_price: item?.unit_price,
									total: total,
									limit_quantity: 0,
									received_quantity: 0,
									receiving_quantity: 0,
									isMax: item?.quantity === received_quantity ? true : false,
								};
							});
							setSelectedItems(populate);
						}
					}
					setIsLoading(false);
				});
		} else {


			api.getNextReferenceNumber('receipt').then((res) => {
				const next_reference_no = res?.data?.next_reference_no
				setNextReference(next_reference_no)
			});

			api.getPurchaseOrderDetails(id).then((res) => {

				const info = {
					id: res?.data?.id,
					purchase_order_id: res?.data?.purchase_order_id,
					purchase_invoice_number: res?.data?.purchase_invoice_number,
					purchase_invoice_id: res?.data?.id,
					date: res?.data?.date,
					currency: res?.data?.currency,
					supplier_id: res?.data?.supplier_id,
					supplier_name: res?.data?.supplier?.name,
					warehouse_id: res?.data?.warehouse_id,
					warehouse_name: res?.data?.warehouse?.name,
					created_by: res?.data?.created_by,
					purchase_invoice_items: res?.data?.purchase_invoice_items,
					attachmentExtension: res?.data?.default_attachment?.extension,
					attachmentUrl: res?.data?.default_attachment?.url,
					attachmentId: res?.data?.default_attachment?.id,
				}
				setPurchaseInvoice(info)
				if (res?.data?.master_status?.name === 'Received') {
					const receiptItems = res?.data?.receipts.map(receipt => receipt.receipt_items).flat();
					const sumsByItemId = receiptItems.reduce((accumulator, currentItem) => {
						const itemId = currentItem.purchase_invoice_item_id;
						const quantity = currentItem.quantity;
						accumulator[itemId] = (accumulator[itemId] || 0) + quantity;
						return accumulator;
					}, {});
				}
				
				if (res?.data?.purchase_invoice_items.length > 0) {


					const populate = res?.data?.purchase_invoice_items?.map(item => {
						const limitQuantity = item.quantity - (item.received_quantity ?? 0);
						
							return {
								id: item.id,
								product_id: item.product?.id,
								sku: item.product?.sku,
								product_name: item.product?.name,
								color_id: item?.selectedColorId,
								color_name: item?.selectedColorName,
								thumbnail_url: item.product?.thumbnail_url,
								custom_thumbnail_url: item.product?.custom_thumbnail_url,
								quantity: item.quantity,
								limit_quantity: limitQuantity,
								received_quantity: item.received_quantity ?? 0,
								receiving_quantity: 0,
								isMax: item?.quantity === item?.received_quantity ? true : false,
							};
					}).filter(item => item.limit_quantity > 0);
					setSelectedItems(populate);
				}
				setIsLoading(false);
			});
		}

	}
    const handleIncrease = (productId, colorId) => {
        
        const itemCollected = selectedItems.map((item) => {
            if (item.product_id === productId && item.color_id === colorId) {
                const receivingQuantity = item.receiving_quantity + 1;
                if (receivingQuantity > item.limit_quantity) {
                    toast.error('You can not receive more than the ordered quantity');
                    return item;
                }
                const isMax = (receivingQuantity + item.received_quantity) === item.quantity;
				return {
					...item,
					receiving_quantity: receivingQuantity,
					isMax: isMax,
				};
            }
            return item;
        });
        setSelectedItems(itemCollected);
	};

    const handleDecrease = (productId, colorId) => {
        const itemCollected = selectedItems.map((item) => {
            if (item.product_id === productId && item.color_id === colorId) {
                const receivingQuantity = item.receiving_quantity - 1;
                if (receivingQuantity < 0) {
                    toast.error('You cannot receive less than 0');
                    return item;
                }
                const isMax = (receivingQuantity + item.received_quantity) === item.quantity;
				return {
					...item,
					receiving_quantity: receivingQuantity,
					isMax: isMax
				};
            }
            return item;
        });
        setSelectedItems(itemCollected);

	};

    const handleChangeQuantity = (productId, value, colorId) => {
        const receivingQuantity = parseInt(value);
        if (isNaN(receivingQuantity)) {
            const itemCollected = [...selectedItems];
            const itemIndex = selectedItems.findIndex((item) => item.product_id === productId && item.color_id === colorId);
            itemCollected[itemIndex].receiving_quantity = 0;
            setSelectedItems(itemCollected);
        } else {
            const itemCollected = selectedItems.map((item) => {
                if (item.product_id === productId) {
                    if (receivingQuantity < 0) {
                        toast.error('You cannot receive less than 0');
                        return item;
					} else if (receivingQuantity > item?.limit_quantity) {

						toast.error('You can not receive more than the ordered quantity');
						return { ...item, receiving_quantity: item?.limit_quantity };
                    }
                    return { ...item, receiving_quantity: receivingQuantity };
                }
                return item;
            });
            setSelectedItems(itemCollected);
        }
    }
	const handleAddAll = (productId, colorId) => {
        const collection = [...selectedItems];
        const itemCollected = collection?.map((item) => {
            if (item?.product_id === productId && item?.color_id === colorId) {
				const receivingQuantity = item.quantity - (item.received_quantity ?? 0);
                const isMax = (receivingQuantity + item.received_quantity) === item.quantity;
                return {
                    id: item.id,
                    product_id: item.product_id,
                    sku: item.sku,
					product_name: item.product_name,
					color_id: item.color_id,
					color_name: item.color_name,
                    thumbnail_url: item.thumbnail_url,
                    custom_thumbnail_url: item.custom_thumbnail_url,
                    quantity: item.quantity,
                    limit_quantity: item.limit_quantity,
                    received_quantity: item.received_quantity ?? 0,
					receiving_quantity: receivingQuantity,
					isMax: isMax
              };
            }
            return item;
        });
        setSelectedItems(itemCollected);
	}
	
	
	const handleProcessReceipt = () => {
		setShow(true);
	}
    
    const handleClose = () => {
        setShow('')
    }

    const submitReceiptForm = (type) => {
        if (type === 'without_imei') {

            let data = {
                purchase_invoice: purchaseInvoice,
                items: selectedItems
            };
            
            toast.promise(
                api
                    .submitPurchaseOrderReceipt(data)
                    .then(
                        (res) => {
                            if (res.status) {
                                history(`/purchasing/view/${purchaseInvoice?.id}`);
                            }
    
                            return res?.status ? res : null;
                        },
                        () => { }
                    ),
                {
                    loading: 'Completing purchase order...',
                    success: (data) => `${data.message}`,
                    error: <b>Failed to receive purchase order item</b>
                }
            );
        } else {
            
        }
    }
    
	
	const viewData = (type, id) => {
		if (type === 'products') {
			window.open(`/products/${id}`, '_blank');
			// history(`/products/${id}`);
		} else if (type === 'purchase_invoice') {
			window.open(`/purchasing/view/${id}`, '_blank');
		} else if (type === 'supplier') {
			window.open(`/contacts/${id}`, '_blank');
		} else if (type === 'warehouse') {
			window.open(`/warehouses/${id}`, '_blank');
		}
	};

	const isMobile = useIsMobile();

	return (
		<>
			<div className="row">
				<div className="col-12 col-md-12">
					<div className="show-on-mobile new-page-header" style={{ position: 'relative' }}>
						<div className="header-bubble" />
						<div className="d-flex flex-column gap-2 " style={{ position: 'relative', zIndex: 1, paddingTop: 10, width: '100%' }}>
							<div
								style={{
									position: 'relative',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									minHeight: 38 // ensures enough height for the button
								}}
							>
								{' '}
								<button
									type="button"
									aria-label="Back"
									onClick={() => history(-1)}
									className="circle-back-btn"
									style={{
										position: 'absolute',
										left: 20,
										top: '50%',
										transform: 'translateY(-50%)'
									}}
								>
									{/* Replace with your fi arrow icon, example below */}
									<svg width="22" height="22" fill="none" viewBox="0 0 24 24">
										<path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
								</button>
								<div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
									<h5 style={{ textAlign: 'center', flex: 1, margin: 0 }}>{nextReference}</h5>
								</div>
							</div>
							<div style={{ display: 'flex', justifyContent: 'center', gap: 5, padding: '0px 20px', alignItems: 'center' }}>
								<button
									className="btn btn-primary btn-outlined-main-primary"
									disabled={
										isNaN(parseInt(selectedItems.reduce((sum, current) => sum + parseInt(current.receiving_quantity), 0)))
											? 0
											: parseInt(selectedItems.reduce((sum, current) => sum + parseInt(current.receiving_quantity), 0)) === 0
									}
									onClick={() => handleProcessReceipt()}
								>
									<FaCheck /> Add products to stock
								</button>
							</div>
						</div>
					</div>
					<div
						className="section-head row hide-on-mobile"
						style={{
							padding: '0px 10px 20px 10px'
						}}
					>
						
						<div className="row">
							<div className="col-12">
								<div
									style={{
										display: 'flex',
										width: '100%',
										margin: '10px 0 -10px 0'
									}}
								>
									<button
										type="button"
										className="btn btn-primary btn-outlined-main white-var"
										style={{ minWidth: 'unset' }}
										onClick={() => {
											history(`/purchasing/view/${id}`);
										}}
									>
										<ChevronLeftIcon /> Back to Purchase Invoice
									</button>
								</div>
							</div>
						</div>
					</div>
					<div className="row m-0 mobile-scroll-container">
						<div className={` mb-4 p-0 p-xl-4 w-100 ${isMobile ? 'pt-4' : 'card shadow-sm rounded bg-white'}`}>
							{!isMobile ? (
								<Scroller>
									<div className="row">
										<div className="col-12">
											<div className="row">
												<div className="col-12 col-md-6">

													<div className="" style={{ justifyContent: 'space-between' }}>
														<table className="projectmix-table-vertical">
															<tbody>
																<tr>
																	<th style={{ width: '157px' }}>Receipt</th>
																	<td>
																		{
																			isLoading ? (
																				<div style={{ width: '150px' }}>
																					<Skeleton variant="rounded" width={'100%'} height={22} style={{ borderRadius: '8px' }} />
																				</div>
																			) : (
																				`${nextReference}`
																			)
																		}
																	</td>
																</tr>
																<tr>
																	<th style={{ width: '157px' }}>Purchase Invoice #</th>
																	<td
																		className={hasEveryPermission(['View Purchase Invoice (Button)']) ? 'cursor-pointer text-primary' : ''}
																		onClick={() => {
																			if (hasEveryPermission(['View Purchase Invoice (Button)'])) {
																				viewData('purchase_invoice', purchaseInvoice?.purchase_invoice_id);
																			}
																		}}
																	>
																		{
																			isLoading ? (
																				<div style={{ width: '150px' }}>
																					<Skeleton variant="rounded" width={'100%'} height={22} style={{ borderRadius: '8px' }} />
																				</div>
																			) : (
																				`${purchaseInvoice?.purchase_invoice_number}`
																			)
																		}
																	</td>
																</tr>
																<tr>
																	<th style={{ width: '157px' }}>Supplier</th>
																	<td
																		className={hasEveryPermission(['View Contact (Button)']) ? 'cursor-pointer text-primary' : ''}
																		onClick={() => {
																			if (hasEveryPermission(['View Contact (Button)'])) {
																				viewData('supplier', purchaseInvoice?.supplier_id);
																			}
																		}}
																	>
																		{
																			isLoading ? (
																				<div style={{ width: '150px' }}>
																					<Skeleton variant="rounded" width={'100%'} height={22} style={{ borderRadius: '8px' }} />
																				</div>
																			) : (
																				`${purchaseInvoice?.supplier_name}`
																			)
																		}
																	</td>
																</tr>
																<tr>
																	<th style={{ width: '157px' }}>Warehouse</th>
																	<td
																		className={hasEveryPermission(['View Warehouse (Button)']) ? 'cursor-pointer text-primary' : ''}
																		onClick={() => {
																			if (hasEveryPermission(['View Warehouse (Button)'])) {
																				viewData('warehouse', purchaseInvoice?.warehouse_id);
																			}
																		}}
																	>
																		{
																			isLoading ? (
																				<div style={{ width: '150px' }}>
																					<Skeleton variant="rounded" width={'100%'} height={22} style={{ borderRadius: '8px' }} />
																				</div>
																			) : (
																				`${purchaseInvoice?.warehouse_name}`
																			)
																		}
																	</td>
																</tr>
																<tr>
																	<th style={{ width: '157px' }}>Purchase Date</th>
																	<td>
																		{
																			isLoading ? (
																				<div style={{ width: '150px' }}>
																					<Skeleton variant="rounded" width={'100%'} height={22} style={{ borderRadius: '8px' }} />
																				</div>
																			) : (
																				<>
																					{moment(purchaseInvoice?.date).format('DD MMM YYYY')}{' '}
																					{purchaseInvoice?.created_by && `by ${purchaseInvoice?.created_by}`}
																				</>
																			)
																		}
																	</td>
																</tr>
																<tr>
																	<th style={{ width: '157px' }}>Currency</th>
																	<td className="upper-case"> 
																		{
																			isLoading ? (
																				<div style={{ width: '150px' }}>
																					<Skeleton variant="rounded" width={'100%'} height={22} style={{ borderRadius: '8px' }} />
																				</div>
																			) : (
																				`${purchaseInvoice?.currency}`
																			)
																		}
																	</td>
																</tr>
															</tbody>
														</table>
													</div>
												</div>
												<div className="col-12 col-md-6 flex justify-center align-center ">
													{
														purchaseInvoice?.attachmentId && (
															<div
																className="uploaded-attachment-preview"
																onMouseEnter={() => setShowAttachmentActions(true)}
																onMouseLeave={() => setShowAttachmentActions(false)}
																style={{ position: 'relative' }}
															>

																{/* <div className="card-body text-center" style={{ padding: '0px', height: '320px', width: '300px', }}> */}
																<div className="card-body text-center" style={{ padding: '0px' }}>
																	{purchaseInvoice?.attachmentExtension === 'pdf' ? (
																			<iframe
																			src={`${purchaseInvoice?.attachmentUrl}#toolbar=0&navpanes=0&zoom=FitW`}
																			style={{
																				width: '200px',
																				// minHeight: '320px',
																				// height: isMobile ? '320px' : '600px',
																				height: '185px',
																				border: 'none',
																				borderRadius: 8,
																				background: '#fff',
																				display: 'block'
																			}}
																			title="PDF Preview"
																			/>
																		// <PdfViewer src={attachmentPreviewDetails?.url + '#toolbar=0&navpanes=0&zoom=FitW'} style={{ height: '320px'}} />
																	) : (
																		<img src={purchaseInvoice?.attachmentUrl} className="img-fluid" />
																	)}
																	{showAttachmentActions && (
																		<div
																			style={{
																				position: 'absolute',
																				display: 'flex',
																				justifyContent: 'center',
																				alignItems: 'center',
																				height: '100%',
																				gap: '10px',
																				top: 0,
																				left: 0,
																				width: '100%',
																				background: 'rgba(255,255,255,0.7)',
																				zIndex: 2,
																			}}
																		>
																			<a
																				href={`${purchaseInvoice?.attachmentUrl}`}
																				target="_blank"
																				className="btn btn-primary btn-outlined-main"
																			>
																				Download
																			</a>
																			<button
																				type="button"
																				className="btn btn-danger flex items-center"
																				onClick={() => removeAttachment(purchaseInvoice?.attachmentId)}
																			>
																				Delete
																			</button>
																		</div>
																	)}
																</div>
															</div>
														)
													}
												</div>
											</div>
											<div className="productsTable estlist" style={{ fontSize: '12px' }}>
												<div className="section-head mb-2">
													<h2 style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '18px' }}>Products</h2>
												</div>
												<div className="estimation-table-area">
													<div className="table-side">
														<div className="table-responsive-xl tableData">
															{edit ? (
																<Scroller>
																	<table className="table pm-table">
																		<thead>
																			<tr>
																				<th className="align-middle" style={{ width: '' }}></th>
																				<th className="align-middle" style={{ width: '' }}>
																					SKU
																				</th>
																				<th className="align-middle">Product</th>
																				<th className="align-middle" style={{ width: '' }}>
																					Received
																				</th>
																				<th className="align-middle" style={{ width: '' }}>
																					Value
																				</th>
																				<th className="align-middle" style={{ width: '' }}>
																					Total
																				</th>
																			</tr>
																		</thead>
																		<tbody>
																			{
																				isLoading ? (
																					[...Array(10)].map((e, i) => (
																						<tr>
																							{[...Array(6)].map((e, i) => (
																								<td>
																									<Skeleton variant="rounded" width={'100%'} height={43}></Skeleton>
																								</td>
																							))}
																						</tr>
																					))
																				) : (
																					selectedItems?.length > 0 ? (
																						selectedItems.map((item, index) => (
																							<tr key={item?.id}>
																								<td className="text-center">
																									{item?.thumbnail_url ? (
																										<img src={item.thumbnail_url} style={{ width: '40px' }} alt="product" />
																									) : item?.custom_thumbnail_url ? (
																										<img src={item.custom_thumbnail_url} style={{ width: '40px' }} alt="product" />
																									) : (
																										<img src={NoImage} style={{ width: '40px' }} alt="product" />
																									)}
																								</td>
																								<td
																									className={
																										hasEveryPermission(['View Product (Button)'])
																											? 'cursor-pointer text-primary align-middle'
																											: 'align-middle'
																									}
																									onClick={() => {
																										if (hasEveryPermission(['View Product (Button)'])) {
																											viewData('products', item.product_id);
																										}
																									}}
																								>
																									{item?.sku}
																								</td>
																								<td className="align-middle">{item?.product_name} {item?.color_name}</td>
																								<td className="align-middle">{item?.quantity}</td>
																								<td className="align-middle">
																									{getCurrencySymbol(purchaseInvoice?.currency)}{' '}
																									{parseFloat(item?.unit_price)
																										.toFixed(2)
																										.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
																								</td>
																								<td className="align-middle">
																									{getCurrencySymbol(purchaseInvoice?.currency)}{' '}
																									{parseFloat(item?.total)
																										.toFixed(2)
																										.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
																								</td>
																							</tr>
																						))
																					) : (
																						<tr className="no-hover">
																							<td colSpan={6}>
																								<div className="not-found-container cstm-not-found">
																									<div className="not-found-icon">
																										<TbDatabaseExclamation />
																									</div>
																									<h6 className="not-found-text">Products not found</h6>
																								</div>
																							</td>
																						</tr>
																					)	
																				)
																			}
																		</tbody>
																		<tfoot>
																			<tr>
																				<td colSpan={3}></td>
																				<td>
																					{selectedItems?.length > 0 && selectedItems.reduce((sum, current) => sum + current.quantity, 0)}{' '}
																					pcs
																				</td>
																				<td></td>
																				<td>
																					{getCurrencySymbol(purchaseInvoice?.currency)}{' '}
																					{selectedItems?.length > 0 &&
																						parseFloat(selectedItems.reduce((sum, current) => sum + current.total, 0))
																							.toFixed(2)
																							.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
																				</td>
																			</tr>
																		</tfoot>
																	</table>
																</Scroller>
															) : (
																<Scroller>
																	<table className="table table-md text-gray-900 formActBtns border-0">
																		<thead>
																			<tr>
																				<th className="align-middle" style={{ width: '50px' }}></th>
																				<th className="align-middle" style={{ width: '150px' }}>
																					SKU
																				</th>
																				<th className="align-middle">Product</th>
																				<th className="align-middle" style={{ width: '100px' }}>
																					Ordered
																				</th>
																				<th className="align-middle" style={{ width: '100px' }}>
																					Expected
																				</th>
																				<th className="align-middle" style={{ width: '110px' }}>
																					Amount Receiving
																				</th>
																			</tr>
																		</thead>
																		<tbody>
																				
																			{
																				isLoading ? (
																					[...Array(10)].map((e, i) => (
																						<tr>
																							{[...Array(6)].map((e, i) => (
																								<td>
																									<Skeleton variant="rounded" width={'100%'} height={43}></Skeleton>
																								</td>
																							))}
																						</tr>
																					))
																				) : (
																					selectedItems?.length > 0 ? (
																						selectedItems.map((item, index) => (
																							<tr key={item?.id}>
																								<td className="text-center">
																									{item?.thumbnail_url ? (
																										<img src={item.thumbnail_url} style={{ width: '30px' }} alt="product" />
																									) : item?.custom_thumbnail_url ? (
																										<img src={item.custom_thumbnail_url} style={{ width: '30px' }} alt="product" />
																									) : (
																										<img src={NoImage} style={{ width: '30px' }} alt="product" />
																									)}
																								</td>
																								<td
																									className={
																										hasEveryPermission(['View Product (Button)'])
																											? 'cursor-pointer text-primary align-middle'
																											: 'align-middle'
																									}
																									onClick={() => {
																										if (hasEveryPermission(['View Product (Button)'])) {
																											viewData('products', item.product_id);
																										}
																									}}
																								>
																									{item?.sku}
																								</td>
																								<td className="align-middle">{item?.product_name} {item?.color_name} </td>
																								<td className="align-middle">{item?.quantity}</td>
																								<td className="align-middle">
																									{item?.limit_quantity}
																								</td>
																								<td className="align-middle">
																									<div className="">
																										<>
																											<div className="btn-group">
																												<button
																													className="btn btn-default btn-sm btn-left"
																													onClick={() => handleDecrease(item?.product_id, item?.color_id)}
																												>
																													<FaMinus />
																												</button>
																												<input
																													type="text"
																													value={item?.receiving_quantity}
																													className="product-amount-field"
																													onChange={(e) => handleChangeQuantity(item?.product_id, e.target.value, item?.color_id)}
																												></input>
																												<button
																													className="btn btn-default btn-sm btn-right"
																													style={{ borderRight: 'none' }}
																													onClick={() => handleIncrease(item?.product_id, item?.color_id)}
																												>
																													<FaPlus />
																												</button>
																												<button
																													className="btn btn-default btn-right"
																													onClick={() => handleAddAll(item?.product_id, item?.color_id)}
																													style={{
																														background: item?.isMax ? '#2ba747' : undefined,
																														color: item?.isMax ? '#fff' : undefined
																													}}
																												>
																													<FaCheck />
																												</button>
																											</div>
																										</>
																									</div>
																								</td>
																							</tr>
																						))
																					) : (
																						<tr className="no-hover">
																							<td colSpan={9}>
																								<div className="not-found-container cstm-not-found">
																									<div className="not-found-icon">
																										<TbDatabaseExclamation />
																									</div>
																									<h6 className="not-found-text">Products not found</h6>
																								</div>
																							</td>
																						</tr>
																					)
																				)
																			}
																		</tbody>
																	</table>
																</Scroller>
															)}
														</div>
													</div>
												</div>
											</div>
											{purchaseInvoice?.purchase_invoice_items?.length > 0 ? (
												<div className="section-footer">
													<div className="footer-left">
														<button
															className="btn btn-primary btn-outlined-main-primary"
															disabled={
																isNaN(parseInt(selectedItems.reduce((sum, current) => sum + parseInt(current.receiving_quantity), 0)))
																	? 0
																	: parseInt(selectedItems.reduce((sum, current) => sum + parseInt(current.receiving_quantity), 0)) === 0
															}
															onClick={() => handleProcessReceipt()}
														>
															<FaCheck /> Add products to stock
														</button>
													</div>
													<div className="footer-left">
														<div className="produt-count">
															<label htmlFor=""> Amount Expected</label>
															<label>
																{selectedItems.reduce((sum, current) => sum + current.quantity, 0) -
																	selectedItems.reduce((sum, current) => sum + current.received_quantity, 0)}
															</label>
														</div>
														<div className="price">
															<label htmlFor=""> Amount Receiving </label>
															<label>
																{isNaN(parseInt(selectedItems.reduce((sum, current) => sum + parseInt(current.receiving_quantity), 0)))
																	? 0
																	: parseInt(selectedItems.reduce((sum, current) => sum + parseInt(current.receiving_quantity), 0))}
															</label>
														</div>
													</div>
												</div>
											) : (
												<></>
											)}
										</div>
									</div>
								</Scroller>
							) : (
								<div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
									<div className="m-0 p-3 p-xl-4 w-100 new-card">
										<div style={{ display: 'flex', flexDirection: 'column' }}>
											<div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
												<div className="textGray">Receipt</div>
												<div style={{ fontWeight: 500 }}>{nextReference}</div>
											</div>
											<div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
												<div className="textGray">Purchase Order</div>
												<div
													style={{ fontWeight: 500 }}
													className={hasEveryPermission(['View Purchase Invoice (Button)']) ? 'cursor-pointer text-primary' : ''}
													onClick={() => {
														if (hasEveryPermission(['View Purchase Invoice (Button)'])) {
															viewData('purchase_invoice', purchaseInvoice?.purchase_invoice_id);
														}
													}}
												>
													{purchaseInvoice?.purchase_order_id}
												</div>
											</div>
											<div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
												<div className="textGray">Supplier</div>
												<div
													style={{ fontWeight: 500 }}
													className={hasEveryPermission(['View Contact (Button)']) ? 'cursor-pointer text-primary' : ''}
													onClick={() => {
														if (hasEveryPermission(['View Contact (Button)'])) {
															viewData('supplier', purchaseInvoice?.supplier_id);
														}
													}}
												>
													{purchaseInvoice?.supplier_name}
												</div>
											</div>
											<div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
												<div className="textGray">Warehouse</div>
												<div
													style={{ fontWeight: 500 }}
													className={hasEveryPermission(['View Warehouse (Button)']) ? 'cursor-pointer text-primary' : ''}
													onClick={() => {
														if (hasEveryPermission(['View Warehouse (Button)'])) {
															viewData('warehouse', purchaseInvoice?.warehouse_id);
														}
													}}
												>
													{purchaseInvoice?.warehouse_name}
												</div>
											</div>
											<div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
												<div className="textGray">Purchased Date</div>
												<div style={{ fontWeight: 500 }}>
													{' '}
													{moment(purchaseInvoice?.date).format('DD MMM YYYY')}{' '}
													{purchaseInvoice?.created_by && `by ${purchaseInvoice?.created_by}`}
												</div>
											</div>
											<div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
												<div className="textGray">Currency</div>
												<div style={{ fontWeight: 500 }}>{purchaseInvoice?.currency}</div>
											</div>
										</div>
									</div>
									<div className="m-0 p-3 p-xl-4 w-100 new-card">
										<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
											<label htmlFor=""> Amount Expected</label>
											<label>
												{selectedItems.reduce((sum, current) => sum + current.quantity, 0) -
													selectedItems.reduce((sum, current) => sum + current.received_quantity, 0)}
											</label>
										</div>
										<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
											<label htmlFor=""> Amount Receiving </label>
											<label>
												{isNaN(parseInt(selectedItems.reduce((sum, current) => sum + parseInt(current.receiving_quantity), 0)))
													? 0
													: parseInt(selectedItems.reduce((sum, current) => sum + parseInt(current.receiving_quantity), 0))}
											</label>
										</div>
									</div>
									<div style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid #eee' }}>
										{selectedItems?.length > 0 ? (
											selectedItems.map((item, index) => (
												<div
													key={item?.id}
													style={{
														display: 'flex',
														justifyContent: 'space-between',
														background: index % 2 === 0 ? '#f9f9f9' : '#fff',
														borderRadius: 8,
														padding: '10px 0px',
														borderBottom: '1px solid #eee'
													}}
												>
													<div style={{ flex: 1 }}>
														<div style={{ fontWeight: 500, fontSize: 14, marginBottom: 2 }}>{item?.product_name} {item?.color_name}</div>
														<div style={{ color: '#888', fontSize: 13, marginBottom: 2 }}>
															<span
																className={hasEveryPermission(['View Product (Button)']) ? 'cursor-pointer text-primary' : ''}
																style={{ marginRight: 8 }}
																onClick={() => {
																	if (hasEveryPermission(['View Product (Button)'])) {
																		viewData('products', item.product_id);
																	}
																}}
															>
																{item?.sku}
															</span>
														</div>

														<div style={{ display: 'flex', gap: 10, fontSize: 12, marginBottom: 2 }}>
															<div>
																Ordered: <b>{item?.quantity}</b>
															</div>
															<div>
																Expected: <b>{item?.limit_quantity}</b>
															</div>
														</div>
														<div className="btn-group">
															<button className="btn btn-default btn-sm btn-left" onClick={() => handleDecrease(item?.product_id, item?.color_id)}>
																<FaMinus />
															</button>
															<input
																type="text"
																value={item?.receiving_quantity}
																className="product-amount-field"
																onChange={(e) => handleChangeQuantity(item?.product_id, e.target.value, item?.color_id)}
															></input>
															<button
																className="btn btn-default btn-sm btn-right"
																style={{ borderRight: 'none' }}
																onClick={() => handleIncrease(item?.product_id, item?.color_id)}
															>
																<FaPlus />
															</button>
															<button
																className="btn btn-default btn-right"
																onClick={() => handleAddAll(item?.product_id, item?.color_id)}
																style={{
																	background: item?.isMax ? '#2ba747' : undefined,
																	color: item?.isMax ? '#fff' : undefined
																}}
															>
																<FaCheck />
															</button>
														</div>
													</div>
													<div>
														{item?.thumbnail_url ? (
															<img src={item.thumbnail_url} style={{ width: '100%', height: 100 }} alt="product" />
														) : item?.custom_thumbnail_url ? (
															<img src={item.custom_thumbnail_url} style={{ width: '100%', height: 100 }} alt="product" />
														) : (
															<img src={NoImage} style={{ width: '100%', height: 100 }} alt="product" />
														)}
													</div>
												</div>
											))
										) : (
											<div style={{ textAlign: 'center', color: '#888', padding: 24 }}>
												<div className="not-found-icon" style={{ fontSize: 32, marginBottom: 8 }}>
													<TbDatabaseExclamation />
												</div>
												<div style={{ fontSize: 15 }}>Products not found</div>
											</div>
										)}
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			{show && !isMobile && (
				<ReceiptModal
					openPopup={show}
					purchaseInvoice={purchaseInvoice}
					handleClose={handleClose}
					submitReceiptForm={submitReceiptForm}
					selectedItems={selectedItems}
				/>
			)}
			{show && isMobile && (
				<ReceiptModalMobile
					openPopup={show}
					purchaseInvoice={purchaseInvoice}
					handleClose={handleClose}
					submitReceiptForm={submitReceiptForm}
					selectedItems={selectedItems}
				/>
			)}
		</>
	);
};

export default PurchasingReceiptForm;
